"use client"
import styled from "@emotion/styled"
import { PointsOfInterestDto } from "api"
import Image from "next/image"
import { useState } from "react"
import "swiper/css"
import "swiper/css/navigation"
import "swiper/css/pagination"
import { Navigation, Pagination } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"

const StyledMobileMapBottomInfo = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 40;
    background: #fff;
    display: flex;
    flex-direction: column;
    @media (min-width: 900px) {
        display: none;
    }
`;

const StyledText = styled.h2`
    color: #000000;
    font-weight: 500;
    margin: 0;
`;

const StyledSelectedLocationHeader = styled.div`
    padding: 16px 24px 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;


const StyledNavigationArrows = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const StyledNavArrow = styled.button`
    cursor: pointer;
    background-color: transparent;
    border: none;

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }

    img {
        transition: opacity 0.2s ease;
    }
`;

const StyledStationsSwiper = styled(Swiper)`
    padding: 16px;
    width: 100%;

    .swiper-slide {
        height: auto;
        margin-left: 12px;
        width: 234px !important;
    }
`;

const StyledSwiperSlide = styled(SwiperSlide)`
    border-radius: 12px;
    border: 1px solid #B3C0CE;
    padding: 16px 8px;
    background: #fff;
    width: 234px;
    cursor: pointer;
    display: flex;
    flex-direction:column;
    &:hover {
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        transform: translateY(-1px);
    }
`;

const StyledHeader = styled.div`
    display: flex;
    align-items: flex-start;
    gap:8px;
`
const StyledNameAndAddress = styled.div`
    display: flex;
    flex-direction: column;
`
const StyledStationTitle = styled.div`
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 4px;
`;

const StyledStationSubtitle = styled.div`
    font-size: 16px;
    font-weight: 400;
    margin-bottom: 12px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StyledMoreInfoLink = styled.div`
    font-size: 14px;
    font-weight: 400;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    justify-content: flex-end;
    margin-top: auto;
    &:hover {
        text-decoration: underline;
    }

    svg {
        width: 12px;
        height: 12px;
        fill: currentColor;
    }
`;

interface MobileBottomInfoProps {
    selectedItem: PointsOfInterestDto | null
    selectedMarkerId: string | null
    nearbyStations: any[]
    onStationClick?: (station: any) => void
    type?: "sell" | "rent"
}

export default function MobileBottomInfo({
    selectedItem,
    selectedMarkerId,
    nearbyStations,
    onStationClick,
    type
}: MobileBottomInfoProps) {
    const [stationsSwiper, setStationsSwiper] = useState<any>(null)
    const [isBeginning, setIsBeginning] = useState(true)
    const [isEnd, setIsEnd] = useState(false)

    if (!selectedItem || !selectedMarkerId) {
        return null
    }

    return (
        <StyledMobileMapBottomInfo>
            <StyledSelectedLocationHeader>
                <StyledText>{type === "sell" ? "עוד נכסים למכירה" : "בסביבה"}</StyledText>
                <StyledNavigationArrows>
                    <StyledNavArrow
                        onClick={() => stationsSwiper?.slidePrev()}
                        disabled={isBeginning || nearbyStations.length <= 1}
                    >
                        <img src="/icons/Arrow/arrow-left.png" alt="Previous" width={24} height={24} />
                    </StyledNavArrow>
                    <StyledNavArrow
                        onClick={() => stationsSwiper?.slideNext()}
                        disabled={isEnd || nearbyStations.length <= 1}
                    >
                        <img src="/icons/Arrow/arrow-right.png" alt="Next" width={24} height={24} />
                    </StyledNavArrow>
                </StyledNavigationArrows>
            </StyledSelectedLocationHeader>

            {nearbyStations.length > 0 && (
                <StyledStationsSwiper
                    modules={[Navigation, Pagination]}
                    spaceBetween={12}
                    slidesPerView="auto"
                    freeMode={true}
                    onSwiper={(swiper) => {
                        setStationsSwiper(swiper)
                        setIsBeginning(swiper.isBeginning)
                        setIsEnd(swiper.isEnd)
                    }}
                    onSlideChange={(swiper) => {
                        setIsBeginning(swiper.isBeginning)
                        setIsEnd(swiper.isEnd)
                    }}
                    className="stations-swiper"
                >
                    {nearbyStations.map((station, index) => (
                        <StyledSwiperSlide
                            key={`station-${station.slug || station.Id || station.id || index}`}
                            onClick={() => onStationClick?.(station)}
                        >
                            <StyledHeader>
                                <Image src="/icons/pazChargeIcon.svg" width={48} height={48} alt="" />
                                <StyledNameAndAddress>
                                    <StyledStationTitle>
                                        {station.stationName || station.ShortName || station.Name || station.name}
                                    </StyledStationTitle>
                                    <StyledStationSubtitle>
                                        {station.address || station.Street}
                                    </StyledStationSubtitle>
                                </StyledNameAndAddress>
                            </StyledHeader>
                            <StyledMoreInfoLink>
                                מידע נוסף ←
                            </StyledMoreInfoLink>
                        </StyledSwiperSlide>
                    ))}
                </StyledStationsSwiper>
            )}
        </StyledMobileMapBottomInfo>
    )
}
